import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { auditFetch, buildAuditHeaders } from '../utils/auditFetch';
import '../styles/Dashboard.css';

const documentLabels = {
  prescription: 'Receta',
  order: 'Orden',
  certificate: 'Certificado',
  referral: 'Referencia',
};

const orderTypeLabels = {
  laboratorio: 'Laboratorio',
  imagen: 'Imagen',
};

const certificateTypeLabels = {
  reposo: 'Reposo',
  asistencia: 'Asistencia',
  condicion: 'Condicion medica',
};

const referralTypeLabels = {
  interconsulta: 'Interconsulta',
  referencia: 'Referencia',
};

const referralStatusLabels = {
  solicitada: 'Solicitada',
  en_revision: 'En revision',
  aceptada: 'Aceptada',
  rechazada: 'Rechazada',
  completada: 'Completada',
  cancelada: 'Cancelada',
};

const documentTypeOrder = ['prescription', 'order', 'certificate', 'referral'];

const purchaseSteps = [
  ['provider', 'Proveedor'],
  ['schedule', 'Agenda'],
  ['delivery', 'Entrega'],
  ['payment', 'Pago'],
];

const purchaseProviders = [
  {
    id: 'lab-central',
    name: 'Lab Central',
    kind: 'laboratorio',
    coverage: 'Domicilio y sede',
    eta: '24-48 h',
    basePrice: 18,
  },
  {
    id: 'diagnostico-andino',
    name: 'Diagnostico Andino',
    kind: 'imagen',
    coverage: 'Centro medico',
    eta: 'Segun agenda',
    basePrice: 35,
  },
  {
    id: 'red-salud-total',
    name: 'Red Salud Total',
    kind: 'mixto',
    coverage: 'Domicilio, sede e imagen',
    eta: '24 h',
    basePrice: 24,
  },
];

const buildDocumentsFromResponses = ({ appointment, prescriptions, orders, certificates, referrals }) => [
  ...(prescriptions || []).map((item) => ({
    id: item.id,
    type: 'prescription',
    appointment,
    title: `${documentLabels.prescription} medica`,
    detail: `${item.medications?.length || 0} medicamento(s)`,
    createdAt: item.createdAt,
    pdfPath: `/api/prescriptions/${item.id}/pdf`,
    indications: [
      ...(item.medications || []).map((medication) => (
        `${medication.name}${medication.dose ? ` ${medication.dose}` : ''}${medication.frequency ? ` - ${medication.frequency}` : ''}${medication.duration ? ` por ${medication.duration}` : ''}`
      )),
      item.generalIndications,
      item.generalWarnings,
    ].filter(Boolean),
  })),
  ...(orders || []).map((item) => ({
    id: item.id,
    type: 'order',
    orderType: item.orderType,
    appointment,
    title: `Orden de ${orderTypeLabels[item.orderType] || 'estudios'}`,
    detail: item.priority ? `Prioridad ${item.priority}` : 'Orden medica',
    createdAt: item.createdAt,
    pdfPath: `/api/medical-orders/${item.id}/pdf`,
    indications: [item.requestedStudies, item.clinicalIndications].filter(Boolean),
  })),
  ...(certificates || []).map((item) => ({
    id: item.id,
    type: 'certificate',
    appointment,
    title: `Certificado de ${certificateTypeLabels[item.certificateType] || 'atencion'}`,
    detail: item.restDays ? `${item.restDays} dia(s) de reposo` : 'Documento medico',
    createdAt: item.createdAt,
    pdfPath: `/api/medical-certificates/${item.id}/pdf`,
    indications: [item.diagnosis, item.observations].filter(Boolean),
  })),
  ...(referrals || []).map((item) => ({
    id: item.id,
    type: 'referral',
    appointment,
    title: `HCU 053 ${referralTypeLabels[item.referralType] || 'Referencia'}`,
    detail: `${item.targetSpecialty || 'Especialidad destino'} - ${referralStatusLabels[item.status] || item.status || 'Solicitada'}`,
    createdAt: item.createdAt,
    pdfPath: `/api/referrals/${item.id}/pdf`,
    indications: [item.targetProvider ? `Medico receptor: ${item.targetProvider}` : '', item.reason, item.requestedAction, item.administrativeNotes].filter(Boolean),
  })),
];

const PatientDocumentsPanel = ({ appointments = [], medicalRecords = [], user }) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const [documents, setDocuments] = useState([]);
  const [statusMessage, setStatusMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [purchaseMessage, setPurchaseMessage] = useState('');
  const [purchaseFlow, setPurchaseFlow] = useState(null);

  const appointmentIds = useMemo(
    () => appointments.map((appointment) => appointment.id).filter(Boolean).join('|'),
    [appointments]
  );

  const groupedDocuments = useMemo(() => (
    documentTypeOrder
      .map((type) => ({
        type,
        label: documentLabels[type],
        documents: documents
          .filter((document) => document.type === type)
          .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)),
      }))
      .filter((group) => group.documents.length > 0)
  ), [documents]);

  const documentCounts = useMemo(() => (
    documentTypeOrder.map((type) => ({
      type,
      label: documentLabels[type],
      count: documents.filter((document) => document.type === type).length,
    }))
  ), [documents]);

  const loadDocuments = useCallback(async () => {
    if (!appointments.length) {
      setDocuments([]);
      return;
    }

    setIsLoading(true);
    setStatusMessage('');
    try {
      if (user.demoMode) {
        const response = await auditFetch(`${backendUrl}/api/demo/presentation`, {}, user);
        const data = response.ok ? await response.json() : { prescriptions: [], referrals: [] };
        const appointmentIdSet = new Set(appointments.map((appointment) => appointment.id));
        const results = appointments.map((appointment) => buildDocumentsFromResponses({
          appointment,
          prescriptions: (data.prescriptions || []).filter((item) => item.appointmentId === appointment.id),
          orders: [],
          certificates: [],
          referrals: (data.referrals || []).filter((item) => item.appointmentId === appointment.id),
        }));
        const flattened = results.flat()
          .filter((document) => appointmentIdSet.has(document.appointment?.id))
          .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        setDocuments(flattened);
        return;
      }

      const results = await Promise.all(appointments.map(async (appointment) => {
        const [
          prescriptionsResponse,
          ordersResponse,
          certificatesResponse,
          referralsResponse,
        ] = await Promise.all([
          auditFetch(`${backendUrl}/api/prescriptions/appointment/${appointment.id}`, {}, user),
          auditFetch(`${backendUrl}/api/medical-orders/appointment/${appointment.id}`, {}, user),
          auditFetch(`${backendUrl}/api/medical-certificates/appointment/${appointment.id}`, {}, user),
          auditFetch(`${backendUrl}/api/referrals/appointment/${appointment.id}`, {}, user),
        ]);

        const [
          prescriptionsData,
          ordersData,
          certificatesData,
          referralsData,
        ] = await Promise.all([
          prescriptionsResponse.ok ? prescriptionsResponse.json() : { prescriptions: [] },
          ordersResponse.ok ? ordersResponse.json() : { orders: [] },
          certificatesResponse.ok ? certificatesResponse.json() : { certificates: [] },
          referralsResponse.ok ? referralsResponse.json() : { referrals: [] },
        ]);

        return buildDocumentsFromResponses({
          appointment,
          prescriptions: prescriptionsData.prescriptions,
          orders: ordersData.orders,
          certificates: certificatesData.certificates,
          referrals: referralsData.referrals,
        });
      }));

      setDocuments(results.flat().sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)));
    } catch (error) {
      console.error('Error loading patient documents:', error);
      setStatusMessage('No se pudieron cargar los documentos clinicos.');
    } finally {
      setIsLoading(false);
    }
  }, [appointments, backendUrl, user]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments, appointmentIds]);

  const openPdf = (document) => {
    if (user.demoMode) {
      setStatusMessage('Modo presentacion: documento ficticio disponible solo como vista de junta.');
      return;
    }

    fetch(`${backendUrl}${document.pdfPath}`, { headers: buildAuditHeaders(user) })
      .then((res) => {
        if (!res.ok) throw new Error('No se pudo abrir el PDF');
        return res.blob();
      })
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        window.open(objectUrl, '_blank', 'noopener,noreferrer');
        setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
      })
      .catch((error) => {
        console.error('Error opening patient document:', error);
        setStatusMessage('No se pudo abrir el documento.');
      });
  };

  const buildDocumentUrl = (document) => {
    try {
      return new URL(document.pdfPath, backendUrl || window.location.origin).toString();
    } catch {
      return new URL(document.pdfPath, window.location.origin).toString();
    }
  };

  const shareByWhatsApp = (document) => {
    const text = [
      document.title,
      document.detail,
      `PDF: ${buildDocumentUrl(document)}`,
    ].filter(Boolean).join('\n');
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer');
  };

  const shareByEmail = (document) => {
    const subject = `Documento medico: ${document.title}`;
    const body = [
      'Hola, comparto este documento medico emitido en mi teleconsulta.',
      '',
      document.title,
      document.detail,
      buildDocumentUrl(document),
    ].join('\n');
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const getOrderStudies = (document) => (
    String(document.indications?.[0] || document.title || '')
      .split(/[,;\n]/)
      .map((study) => study.trim())
      .filter(Boolean)
  );

  const compatibleProviders = (document) => purchaseProviders.filter((provider) => (
    provider.kind === 'mixto' || provider.kind === document.orderType
  ));

  const calculatePurchaseEstimate = (flow) => {
    if (!flow) return 0;
    const provider = purchaseProviders.find((item) => item.id === flow.providerId) || purchaseProviders[0];
    const studyCount = Math.max(getOrderStudies(flow.document).length, 1);
    const deliveryFee = flow.deliveryMode === 'domicilio' ? 6 : 0;
    return provider.basePrice + (studyCount * 4) + deliveryFee;
  };

  const startOrderPurchase = (document) => {
    const providers = compatibleProviders(document);
    setPurchaseFlow({
      step: 'provider',
      document,
      providerId: providers[0]?.id || purchaseProviders[0].id,
      deliveryMode: document.orderType === 'imagen' ? 'centro' : 'domicilio',
      appointmentDate: '',
      appointmentTime: '',
      address: '',
      paymentMethod: 'tarjeta',
      confirmed: false,
    });
    setPurchaseMessage('');
  };

  const updatePurchaseFlow = (changes) => {
    setPurchaseFlow((current) => (current ? { ...current, ...changes } : current));
  };

  const movePurchaseStep = (direction) => {
    setPurchaseFlow((current) => {
      if (!current) return current;
      const currentIndex = purchaseSteps.findIndex(([step]) => step === current.step);
      const nextIndex = Math.min(Math.max(currentIndex + direction, 0), purchaseSteps.length - 1);
      return { ...current, step: purchaseSteps[nextIndex][0] };
    });
  };

  const confirmSimulatedPayment = () => {
    if (!purchaseFlow) return;
    const provider = purchaseProviders.find((item) => item.id === purchaseFlow.providerId);
    const estimate = calculatePurchaseEstimate(purchaseFlow);
    setPurchaseMessage(`Pago simulado registrado: ${provider?.name || 'Proveedor'} recibira la orden ${purchaseFlow.document.title} por $${estimate.toFixed(2)}. Integracion real de pasarela pendiente.`);
    setPurchaseFlow((current) => (current ? { ...current, confirmed: true } : current));
  };

  const formatDocumentDate = (createdAt) => (
    createdAt
      ? new Date(createdAt).toLocaleString('es-EC', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
      : 'Fecha no registrada'
  );

  const recordIndications = medicalRecords
    .map((record) => {
      const plan = record.hcu002?.treatmentPlan || {};
      const followUp = record.hcu002?.clinicalFollowUp || {};
      return {
        id: record.id,
        date: record.updatedAt || record.savedAt,
        diagnosis: record.diagnosis || record.hcu002?.diagnoses?.find((diagnosis) => diagnosis.description)?.description || 'Consulta medica',
        therapeutic: plan.therapeutic || record.evolution || '',
        recommendations: plan.recommendations || '',
        followUp: followUp.requiresNextAppointment
          ? `${followUp.nextAppointmentDate || ''} ${followUp.nextAppointmentTime || ''} ${followUp.nextAppointmentReason || ''}`.trim()
          : followUp.evolution || '',
      };
    })
    .filter((item) => item.therapeutic || item.recommendations || item.followUp)
    .slice(0, 5);

  const renderPurchaseFlow = () => {
    if (!purchaseFlow) return null;
    const currentStepIndex = purchaseSteps.findIndex(([step]) => step === purchaseFlow.step);
    const providers = compatibleProviders(purchaseFlow.document);
    const selectedProvider = purchaseProviders.find((provider) => provider.id === purchaseFlow.providerId) || providers[0];
    const estimate = calculatePurchaseEstimate(purchaseFlow);
    const studies = getOrderStudies(purchaseFlow.document);
    const paymentPayload = {
      orderId: purchaseFlow.document.id,
      providerId: purchaseFlow.providerId,
      deliveryMode: purchaseFlow.deliveryMode,
      appointmentDate: purchaseFlow.appointmentDate,
      appointmentTime: purchaseFlow.appointmentTime,
      address: purchaseFlow.address,
      paymentMethod: purchaseFlow.paymentMethod,
      estimatedAmount: estimate,
      currency: 'USD',
      mode: 'mock',
    };

    return (
      <section className="patient-order-purchase-panel">
        <div className="patient-order-purchase-header">
          <div>
            <span>Compra en linea</span>
            <h3>{purchaseFlow.document.title}</h3>
            <p>{purchaseFlow.document.detail} · {purchaseFlow.document.appointment?.doctor?.name || 'Profesional'}</p>
          </div>
          <button type="button" onClick={() => setPurchaseFlow(null)}>Cerrar</button>
        </div>

        <div className="patient-order-purchase-steps">
          {purchaseSteps.map(([step, label], index) => (
            <button
              key={step}
              type="button"
              className={purchaseFlow.step === step ? 'active' : ''}
              onClick={() => updatePurchaseFlow({ step })}
            >
              <strong>{index + 1}</strong>
              <span>{label}</span>
            </button>
          ))}
        </div>

        <div className="patient-order-purchase-body">
          {purchaseFlow.step === 'provider' && (
            <div className="purchase-provider-grid">
              {providers.map((provider) => (
                <button
                  key={provider.id}
                  type="button"
                  className={purchaseFlow.providerId === provider.id ? 'selected' : ''}
                  onClick={() => updatePurchaseFlow({ providerId: provider.id })}
                >
                  <strong>{provider.name}</strong>
                  <span>{provider.coverage}</span>
                  <small>{provider.eta} · desde ${provider.basePrice.toFixed(2)}</small>
                </button>
              ))}
            </div>
          )}

          {purchaseFlow.step === 'schedule' && (
            <div className="purchase-form-grid">
              <label>
                Fecha
                <input
                  type="date"
                  value={purchaseFlow.appointmentDate}
                  onChange={(event) => updatePurchaseFlow({ appointmentDate: event.target.value })}
                />
              </label>
              <label>
                Hora
                <input
                  type="time"
                  value={purchaseFlow.appointmentTime}
                  onChange={(event) => updatePurchaseFlow({ appointmentTime: event.target.value })}
                />
              </label>
              <div className="purchase-study-list">
                <strong>Estudios solicitados</strong>
                {studies.length === 0 ? (
                  <p>La orden no tiene estudios detallados.</p>
                ) : (
                  studies.map((study, index) => <span key={`${study}-${index}`}>{study}</span>)
                )}
              </div>
            </div>
          )}

          {purchaseFlow.step === 'delivery' && (
            <div className="purchase-form-grid">
              <label>
                Modalidad
                <select
                  value={purchaseFlow.deliveryMode}
                  onChange={(event) => updatePurchaseFlow({ deliveryMode: event.target.value })}
                >
                  <option value="domicilio">Toma a domicilio</option>
                  <option value="centro">Atencion en centro</option>
                </select>
              </label>
              <label className="purchase-field-wide">
                Direccion o sede preferida
                <input
                  value={purchaseFlow.address}
                  onChange={(event) => updatePurchaseFlow({ address: event.target.value })}
                  placeholder={purchaseFlow.deliveryMode === 'domicilio' ? 'Direccion para toma de muestra' : 'Sede o zona preferida'}
                />
              </label>
              <div className="purchase-summary-box">
                <strong>{selectedProvider?.name}</strong>
                <span>{selectedProvider?.coverage}</span>
                <small>{selectedProvider?.eta}</small>
              </div>
            </div>
          )}

          {purchaseFlow.step === 'payment' && (
            <div className="purchase-payment-grid">
              <div className="purchase-summary-box">
                <strong>Total estimado</strong>
                <span>${estimate.toFixed(2)}</span>
                <small>No se realizara cobro real en esta maqueta.</small>
              </div>
              <label>
                Metodo
                <select
                  value={purchaseFlow.paymentMethod}
                  onChange={(event) => updatePurchaseFlow({ paymentMethod: event.target.value })}
                >
                  <option value="tarjeta">Tarjeta</option>
                  <option value="transferencia">Transferencia</option>
                  <option value="wallet">Billetera digital</option>
                </select>
              </label>
              <pre>{JSON.stringify(paymentPayload, null, 2)}</pre>
            </div>
          )}
        </div>

        <div className="patient-order-purchase-footer">
          <button type="button" onClick={() => movePurchaseStep(-1)} disabled={currentStepIndex === 0}>
            Anterior
          </button>
          {purchaseFlow.step === 'payment' ? (
            <button type="button" className="patient-document-buy" onClick={confirmSimulatedPayment}>
              Continuar a pago simulado
            </button>
          ) : (
            <button type="button" className="patient-document-buy" onClick={() => movePurchaseStep(1)}>
              Siguiente
            </button>
          )}
        </div>
      </section>
    );
  };

  return (
    <div className="patient-documents-panel">
      <div className="section-header">
        <div>
          <h2>Documentos e indicaciones</h2>
          <p>Recetas, ordenes, certificados, referencias y recomendaciones de tus atenciones.</p>
        </div>
        <button type="button" className="btn btn-secondary" onClick={loadDocuments} disabled={isLoading}>
          {isLoading ? 'Actualizando...' : 'Actualizar'}
        </button>
      </div>

      <div className="patient-document-summary" aria-label="Resumen de documentos del paciente">
        {documentCounts.map((item) => (
          <div key={item.type} className={`patient-document-summary-item patient-document-summary-${item.type}`}>
            <strong>{item.count}</strong>
            <span>{item.label}</span>
          </div>
        ))}
      </div>

      {statusMessage && <p className="clinical-files-message">{statusMessage}</p>}
      {purchaseMessage && <p className="clinical-files-message">{purchaseMessage}</p>}
      {renderPurchaseFlow()}

      <div className="patient-documents-grid">
        <div className="patient-document-list">
          {isLoading ? (
            <p className="clinical-files-empty">Cargando documentos...</p>
          ) : documents.length === 0 ? (
            <div className="patient-documents-empty">
              <strong>Aun no hay documentos emitidos.</strong>
              <p>Cuando el medico confirme recetas, ordenes, certificados o derivaciones, apareceran aqui agrupados por tipo y fecha.</p>
            </div>
          ) : (
            groupedDocuments.map((group) => (
              <section key={group.type} className="patient-document-group">
                <div className="patient-document-group-header">
                  <div>
                    <h3>{group.label}</h3>
                    <p>{group.documents.length} documento(s), ordenados por fecha de emision.</p>
                  </div>
                  <span className={`patient-document-type patient-document-type-${group.type}`}>
                    {group.label}
                  </span>
                </div>
                {group.documents.map((document) => (
                  <article key={`${document.type}-${document.id}`} className="patient-document-card">
                    <div className="patient-document-date">
                      <strong>{formatDocumentDate(document.createdAt).split(',')[0]}</strong>
                      <span>{formatDocumentDate(document.createdAt).split(',')[1] || ''}</span>
                    </div>
                    <div className="patient-document-content">
                      <strong>{document.title}</strong>
                      <span>{document.detail}</span>
                      <small>{document.appointment?.doctor?.name || 'Profesional'}</small>
                      {document.indications.length > 0 ? (
                        <ul>
                          {document.indications.slice(0, 3).map((indication, index) => (
                            <li key={`${document.id}-ind-${index}`}>{indication}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="patient-document-no-indications">Sin indicaciones adicionales registradas.</p>
                      )}
                    </div>
                    <div className="patient-document-actions">
                      <button type="button" onClick={() => openPdf(document)}>
                        Descargar
                      </button>
                      <button type="button" onClick={() => shareByWhatsApp(document)}>
                        WhatsApp
                      </button>
                      <button type="button" onClick={() => shareByEmail(document)}>
                        Correo
                      </button>
                      {document.type === 'order' && (
                        <button type="button" className="patient-document-buy" onClick={() => startOrderPurchase(document)}>
                          Comprar
                        </button>
                      )}
                    </div>
                  </article>
                ))}
              </section>
            ))
          )}
        </div>

        <div className="patient-indications-card">
          <h3>Indicaciones recientes</h3>
          {recordIndications.length === 0 ? (
            <p className="clinical-files-empty">Las indicaciones apareceran cuando el medico cierre la atencion.</p>
          ) : (
            recordIndications.map((item) => (
              <div key={item.id} className="patient-indication-item">
                <strong>{item.diagnosis}</strong>
                {item.therapeutic && <p><span>Plan:</span> {item.therapeutic}</p>}
                {item.recommendations && <p><span>Recomendaciones:</span> {item.recommendations}</p>}
                {item.followUp && <p><span>Seguimiento:</span> {item.followUp}</p>}
                <small>{item.date ? new Date(item.date).toLocaleDateString('es-EC') : 'Sin fecha'}</small>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default PatientDocumentsPanel;
